import ImageWithBasePath from "@/components/image-with-base-path";
import { useEffect, useRef, useState } from "react";

interface DraggableSliderProps {
  beforeImage: string;
  afterImage: string;
  alt?: string;
}

const DraggableSlider: React.FC<DraggableSliderProps> = ({
  beforeImage,
  afterImage,
  alt = "treatment",
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const afterRef = useRef<HTMLDivElement | null>(null);

  const [active, setActive] = useState(false);
  const currentPercentRef = useRef(50);

  const scrollIt = (pageX: number) => {
    const wrapper = wrapperRef.current;
    const scroller = scrollerRef.current;
    const after = afterRef.current;
    if (!wrapper || !scroller || !after) return;

    const rect = wrapper.getBoundingClientRect();
    let relativeX = pageX - rect.left;

    relativeX = Math.max(0, Math.min(relativeX, wrapper.offsetWidth));

    const percent = (relativeX / wrapper.offsetWidth) * 100;

    currentPercentRef.current = percent;

    scroller.style.left = `${percent}%`;

    const offset = 4;
    let afterPercent = percent + offset;
    afterPercent = Math.max(0, Math.min(afterPercent, 100));

    after.style.width = `${afterPercent}%`;
    after.style.overflow = "hidden";
  };

  useEffect(() => {
    const scroller = scrollerRef.current;
    const after = afterRef.current;
    if (!scroller || !after) return;

    scroller.style.left = "44%";
    after.style.width = "44%";
    after.style.overflow = "hidden";
  }, []);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!active) return;
      scrollIt(e.pageX);
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!active) return;
      const touch = e.touches[0];
      if (!touch) return;
      scrollIt(touch.pageX);
    };

    const deactivate = () => setActive(false);

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", deactivate);
    document.addEventListener("mouseleave", deactivate);

    document.addEventListener("touchmove", onTouchMove, { passive: true });
    document.addEventListener("touchend", deactivate);
    document.addEventListener("touchcancel", deactivate);

    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", deactivate);
      document.removeEventListener("mouseleave", deactivate);

      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", deactivate);
      document.removeEventListener("touchcancel", deactivate);
    };
  }, [active]);

  return (
    <div className="treatment-img flex-fill">
      <div
        className="wrapper"
        ref={wrapperRef}
        onMouseDown={() => setActive(true)}
        onTouchStart={() => setActive(true)}
      >
        <div className="before">
          <ImageWithBasePath
            className="content-image"
            src={beforeImage}
            alt={alt}
          />
        </div>
        <div className="after" ref={afterRef}>
          <ImageWithBasePath
            className="content-image"
            src={afterImage}
            alt={alt}
          />
        </div>
        <div
          className="scroller"
          ref={scrollerRef}
          onMouseDown={() => setActive(true)}
          onTouchStart={() => setActive(true)}
        >
          <svg
            className="scroller__thumb"
            xmlns="http://www.w3.org/2000/svg"
            width={100}
            height={100}
            viewBox="0 0 100 100"
          >
            <polygon
              points="0 50 37 68 37 32 0 50"
              style={{ fill: "#fff" }}
            />
            <polygon
              points="100 50 64 32 64 68 100 50"
              style={{ fill: "#fff" }}
            />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default DraggableSlider;
